import { useEffect } from 'react';

import { useUser } from '@/features/auth/model/auth.selectors';
import { useAuthStore } from '@/features/auth/model/auth.store';
import { wsLogger } from '@/shared/lib/logger';

import {
  ConnectionStatus,
  useConnectionStore,
} from '../stores/connection.store';

/**
 * Mirror of `APL-A.3` / `AND-A.3`. When the browser flips back online while
 * the tab stays visible, Ably's own ping timeout (~30s) is too slow — nudge
 * the connection to reconnect immediately if it's currently in a recoverable
 * bad state.
 *
 * Leader-tab only — follower tabs don't hold an Ably session.
 *
 * See docs/REALTIME_RECONCILE_PLAN.md → WEB-A.3.
 */

const RECOVERABLE_STATES: ReadonlySet<ConnectionStatus> = new Set([
  ConnectionStatus.DISCONNECTED,
  ConnectionStatus.SUSPENDED,
  ConnectionStatus.FAILED,
]);

interface UseNetworkRestoreReconnectProps {
  isLeader: boolean;
}

export function useNetworkRestoreReconnect({
  isLeader,
}: UseNetworkRestoreReconnectProps): void {
  const user = useUser();
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isLeader || !user?.id || !token) return;

    const handleOnline = () => {
      const { status, forceReconnect } = useConnectionStore.getState();
      if (!RECOVERABLE_STATES.has(status)) {
        return;
      }
      wsLogger.info('[network] online — nudging Ably reconnect', {
        fromStatus: status,
      });
      forceReconnect();
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [isLeader, user?.id, token]);
}
